<template>
  <div class="manager-company-opportunity-lookup-ui">
    <div class="manager-company-opportunity-lookup-ui__filter">
      <VInput
        v-model="searchQuery"
        :config="{ label: 'Customer reference', type: 'string', inputMode: 'text' }"
        class="slim"
      />
      <VBtn class="slim" type="button" :loading="isLoading" @click="search">Search</VBtn>
    </div>
    <VSpinner v-if="isLoading" class="centered-block" />
    <VFallbackText v-else-if="errorMessage" class="centered">{{ errorMessage }} </VFallbackText>
    <OpportunityLookupResult v-else-if="opportunity" :opportunity="opportunity" />
  </div>
</template>

<script lang="ts" setup>
  // Types
  import type { TManagerCompanyOpportunityLookup } from '~/modules/manager/types';
  import { AxiosError } from 'axios';

  // Components
  import OpportunityLookupResult from '~/modules/manager/company/opportunity-lookup/result/OpportunityLookupResult.vue';
  import VFallbackText from '~/misc/v-fallback-text/VFallbackText.vue';
  import VSpinner from '~/misc/v-spinner/VSpinner.vue';
  import VInput from '~/misc/v-input';
  import VBtn from '~/misc/v-btn';

  // Models
  import { ManagerCompanyModel } from '~/modules/manager/models/company.model';

  const props = defineProps({
    companyId: {
      type: Number as PropType<number | null>,
      default: null,
    },
  });

  const managerCompanyModel: ManagerCompanyModel = new ManagerCompanyModel();

  const isLoading: Ref<boolean> = ref<boolean>(false);
  const errorMessage: Ref<string | null> = ref<string | null>(null);

  const searchQuery: Ref<string> = ref<string>('');
  const opportunity: Ref<TManagerCompanyOpportunityLookup | null> =
    ref<TManagerCompanyOpportunityLookup | null>(null);

  const setOpportunity = (value: TManagerCompanyOpportunityLookup | null) => {
    opportunity.value = value;
  };

  const setIsLoading = (state: boolean) => {
    isLoading.value = state;
  };

  const setErrorMessage = (msg: string | null) => {
    errorMessage.value = msg;
  };

  const search = async () => {
    if (!props.companyId || !searchQuery.value.trim()) return;

    try {
      setErrorMessage(null);
      setIsLoading(true);

      const result: TManagerCompanyOpportunityLookup = await managerCompanyModel.opportunityLookup(
        props.companyId,
        {
          customer_reference: searchQuery.value,
        }
      );

      setOpportunity(result);
    } catch (e) {
      const isNotFound: boolean = e instanceof AxiosError && e.response?.status === 404;

      setErrorMessage(isNotFound ? 'No data found' : 'Something went wrong');
      setOpportunity(null);
    } finally {
      setIsLoading(false);
    }
  };
</script>
